import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { analytics } from 'analytics';
import { useApiContext } from 'contexts/apiContext';
import { useGlobalModalsContext } from 'contexts/globalModalsContext';
import { useSankeyFormContext } from 'contexts/sankeyFormContext';
import { useAppPlatform } from 'utils/app';
import {
  formToSankey,
  removeLeavePagePrompt,
  useCurrentFormSankeyId,
} from 'utils/form';
import { pathToSankey } from 'utils/routes';
import { buildTemplateCopy } from 'utils/sankey';
import { useCurrentUser } from 'utils/user';

// If the user has just signed up via conversion modal and they are not the owner of the current
// Sankey, create a copy of this Sankey for them and redirect them to that page in the background.
export const useCreateAndRedirectSankeyIfJustSignedUp = () => {
  const { isAuthOrConversionModalOpen } = useGlobalModalsContext();
  const { user } = useCurrentUser();
  const { api } = useApiContext();
  const createdWith = useAppPlatform();
  const navigate = useNavigate();
  const { form } = useSankeyFormContext();
  const sankeyId = useCurrentFormSankeyId();

  // True iff the Sankey diagram is present but it's not owned by the user
  const isNonOwnedSankey = !user?.owned_sankeys_ids.includes(sankeyId ?? 0);

  // True iff the user signed in or signed up via the conversion modal and
  // the Sankey diagram in the background exists but is not owned by them.
  // NOTE: This will allow previously signed up users to sign in again in the
  // modal and create new Sankeys this way but that's fine since this is a path
  // most of them won't discover and it doesn't really give them more power than
  // the anonymous users have since they still won't be able to update these diagrams
  // until they subscribe.
  const shouldCreateSankey =
    isAuthOrConversionModalOpen && user && sankeyId && isNonOwnedSankey;

  // Create Sankey and redirect to it in the background if the conditions are met
  useEffect(() => {
    if (shouldCreateSankey) {
      const diagram = buildTemplateCopy(formToSankey(form), user, createdWith);
      api()
        .sankeys.create({ sankeyRequest: diagram })
        .then((res) => {
          removeLeavePagePrompt(); // Make sure we remove the unsaved changes warning before redirecting
          navigate(pathToSankey(res.data.id), { replace: true });
        })
        .catch((error) => {
          analytics.reportError('Error creating initial diagram', {
            diagram,
            error,
          });
        });
    }
  }, [shouldCreateSankey]);
};
